import React, { FC, Key, useEffect, useMemo, useState } from 'react'
import moment from 'moment'
import { Button, Col, Icon, Input, Text } from 'components/common'
import {
  LineHeader,
  SelectedVariant,
  SelectedVariantListCard,
  VariantSelectorModal,
  VariantSelectorModalForm,
} from 'components/bookings'
import {
  OrderLineItem,
  OrderVariantInput,
  ProductAvailabilitiesQuery,
  ProductAvailabilityInput,
  useProductAvailabilitiesLazyQuery,
} from 'lib/graphql/storeadmin'
import { FormInstance } from 'antd'
import { decode as decodeBase64 } from 'base-64'
import { EditFormType } from 'pages/admin/bookings/[id]/edit'
import { SAGA_ARENA_PRODUCT_NAME } from 'config'

type ProductSelectCardProps = {
  availableDataForSagaArena?: ProductAvailabilitiesQuery
  bookingDates: [moment.Moment, moment.Moment]
  checkedKeys: React.Key[]
  calculatePrices: (inputVariants: OrderVariantInput[]) => Promise<void>
  disabled: boolean
  form: FormInstance<VariantSelectorModalForm | EditFormType>
  handleDiffDates: () => Array<string>
  lineItems?: OrderLineItem[]
  onCheck: (checkedKeysValue: Key[]) => void
  rateId: React.MutableRefObject<string>
  selectedVariants: SelectedVariant[]
  setSelectedVariants: React.Dispatch<React.SetStateAction<SelectedVariant[]>>
  timezone: [moment.Moment, moment.Moment]
  isSagaArena: boolean
  isBookingDetail?: boolean
  hasCustomerId?: boolean
}

const ProductSelectCard: FC<ProductSelectCardProps> = React.memo((props) => {
  const {
    availableDataForSagaArena,
    bookingDates,
    checkedKeys,
    calculatePrices,
    disabled,
    form,
    handleDiffDates,
    lineItems,
    onCheck,
    rateId,
    selectedVariants,
    setSelectedVariants,
    timezone,
    isSagaArena,
    isBookingDetail,
    hasCustomerId,
  } = props

  const [
    getAvailability,
    { data: availableData, loading: availabilitiesLoading },
  ] = useProductAvailabilitiesLazyQuery({ fetchPolicy: 'network-only' })

  // メインアリーナとサブアリーナを除く利用可能な施設のリスト
  const facilitiesExcludingArena = useMemo((): ProductAvailabilitiesQuery => {
    if (!availableData || availabilitiesLoading) return
    const edges = availableData.productAvailabilities.edges.filter(
      ({ product }) =>
        product?.productName.jaJp !== SAGA_ARENA_PRODUCT_NAME.MAIN_ARENA &&
        product?.productName.jaJp !== SAGA_ARENA_PRODUCT_NAME.SUB_ARENA
    )
    return {
      productAvailabilities: {
        edges: edges,
      },
    }
  }, [availabilitiesLoading, availableData])

  const [searchValue, setSearchValue] = useState('')
  const [isOpenModal, setIsOpenModal] = useState(false)

  const handleSearchAvailable = () => {
    setIsOpenModal(true)
    const dates = handleDiffDates()
    const input: ProductAvailabilityInput = {
      dates: dates,
      start: timezone[0].format('HH:mm'),
      end: timezone[1].format('HH:mm'),
      productName: searchValue?.replace(/\s/g, ''),
    }
    if (rateId.current) {
      input.rateId = decodeBase64(rateId.current).split(':')[1]
    }
    getAvailability({
      variables: {
        input: input,
      },
    })
  }

  // SAGAアリーナとその他のストアで、利用可能な施設のリストを切り替える
  const data = isSagaArena
    ? availableDataForSagaArena
    : facilitiesExcludingArena

  const newSelectedVariants = useMemo((): SelectedVariant[] => {
    if (data?.productAvailabilities) {
      return data?.productAvailabilities?.edges?.reduce(
        (result, { product }) => {
          product?.variants?.edges?.forEach(({ variant }) => {
            checkedKeys?.forEach((key) => {
              if (variant?.variantId === key) {
                result.push({
                  variantId: variant?.variantId,
                  productName: product?.productName.jaJp,
                  title: variant?.title.jaJp,
                  price: variant?.price,
                  inventory: variant?.inventory,
                  baseTimeUnit: product?.baseTimeUnit,
                })
              }
            })
          })
          return result
        },
        []
      )
    } else if (!checkedKeys?.length) {
      return []
    } else if (lineItems?.length > 0) {
      return lineItems?.map((lineItem) => ({
        variantId: lineItem.variant,
        key: lineItem?.variant,
        title: lineItem?.variantTitle.jaJp,
        productName: lineItem?.name.jaJp,
        price: lineItem?.price,
        specialPrice: lineItem?.specialPrice,
        baseTimeUnit: lineItem?.baseTimeUnit,
      }))
    }
    return []
  }, [checkedKeys, data?.productAvailabilities, lineItems])

  // 選択済みバリエーションリストを更新する
  useEffect(() => {
    if (
      newSelectedVariants &&
      JSON.stringify(selectedVariants) !== JSON.stringify(newSelectedVariants)
    ) {
      setSelectedVariants(newSelectedVariants)
    }
  }, [newSelectedVariants, selectedVariants, setSelectedVariants])
  useEffect(() => {
    setSelectedVariants(newSelectedVariants)
    let inputVariants: OrderVariantInput[] = []
    if (isSagaArena) {
      inputVariants = newSelectedVariants?.map((variant) => {
        return {
          variantId: variant.variantId,
          arenaBookingInfo: variant.arenaBookingInfo,
        }
      })
    } else {
      inputVariants = newSelectedVariants?.map((variant) => {
        return {
          variantId: variant.variantId,
          quantity: 1,
        }
      })
    }
    // 顧客が選択されていたら料金計算を実行する
    if (hasCustomerId) {
      calculatePrices(inputVariants)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkedKeys, setSelectedVariants])

  return (
    <div>
      <Col span={24} className="mb-2 space-x-2">
        <Text strong>予約対象</Text>
      </Col>
      <p className="opacity-60 m-1">
        施設名で検索できます。未入力で検索すると全施設が表示されます。
      </p>
      <div className="grid grid-cols-10 gap-x-2 mb-8">
        <Input
          className="col-span-8"
          allowClear
          autoComplete="off"
          placeholder="ここに施設名を入力"
          prefix={<Icon.SearchOutlined />}
          onChange={(value) => setSearchValue(value.target.value)}
          disabled={disabled}
        />
        <Button
          type="primary"
          className="col-span-2"
          disabled={disabled}
          onClick={handleSearchAvailable}
        >
          検索
        </Button>
      </div>
      {selectedVariants?.length > 0 &&
        timezone?.length > 0 &&
        bookingDates?.length > 0 && (
          <>
            <LineHeader isSagaArena={isSagaArena} />
            <div className="h-px bg-black opacity-10 mb-4" />
            <SelectedVariantListCard
              selectedVariants={selectedVariants}
              period={{
                start: timezone[0].format('HH:mm'),
                end: timezone[1].format('HH:mm'),
              }}
              bookingDates={{
                start: bookingDates[0].format('YYYY-MM-DD'),
                end: bookingDates[bookingDates?.length - 1].format(
                  'YYYY-MM-DD'
                ),
              }}
              isSagaArena={isSagaArena}
              isBookingDetail={isBookingDetail}
            />
          </>
        )}
      <VariantSelectorModal
        form={form}
        loading={availabilitiesLoading}
        data={data}
        open={isOpenModal}
        onClose={setIsOpenModal}
        checkedKeys={checkedKeys}
        onCheck={onCheck}
        lineItems={lineItems}
      />
    </div>
  )
})

export default ProductSelectCard
